import { DeviceType } from '@/src/types'
import { useState } from 'react'
import { FLYIN } from '@/src/constants'
import { Brand } from '@/src/utils/brand'

export const isEmpty = (obj: any): boolean => {
    if (obj instanceof Date) {
        return false
    }
    // null and undefined are "empty"
    if (obj == null) {
        return true
    }

    const isNumber = (value: any): boolean =>
        Object.prototype.toString.call(value) === '[object Number]'
    const isNaN = (value: any): boolean =>
        isNumber(value) && value.toString() === 'NaN'

    if (isNumber(obj)) {
        return isNaN(obj)
    }

    /** Assume if it has a length property with a non-zero value
     * that that property is correct.
     */
    if (obj.length > 0) {
        return false
    }
    if (obj.length === 0) {
        return true
    }

    /** If it isn't an object at this point
     * it is empty, but it can't be anything *but* empty
     * Is it empty?  Depends on your application.
     */
    if (typeof obj !== 'object') {
        return true
    }

    /**  Otherwise, does it have any properties of its own?
     * Note that this doesn't handle
     * toString and valueOf enumeration bugs in IE < 9
     */
    const keys = Object.keys(obj)
    for (let i = 0, key = keys[i]; i < keys.length; i += 1) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            return false
        }
    }
    return true
}

export function getCookie(name: string) {
    if (typeof document !== 'undefined') {
        if (document.cookie) {
            const nameEQ = name + '='
            const ca = document.cookie.split(';')
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i]
                while (c.charAt(0) == ' ') c = c.substring(1, c.length)
                if (c.indexOf(nameEQ) == 0)
                    return c.substring(nameEQ.length, c.length)
            }
            return ''
        } else {
            return ''
        }
    }
}

export const path = (p: (string | number)[], o: any) => {
    const reducerFunction = (xs: any, x: string | number) => {
        return xs && xs[x] ? xs[x] : null
    }
    return p.reduce(reducerFunction, o)
}

export const checkDeviceType = () => {
    const deviceType = null
    if (typeof window === 'undefined') {
        return deviceType
    }
    const userAgent = path(['navigator', 'userAgent'], window)?.toLowerCase()
    const safari = /safari/.test(userAgent) // checks if browser is safari
    const ios = /iphone|ipod|ipad/.test(userAgent) // checks if device is among iphone, ipad or ipod

    if (
        path(['androidData', 'app-agent'], window) &&
        path(['androidData', 'js-version'], window)
    ) {
        return 'AndroidApp'
    }
    if (
        (path(['iosData', 'app-agent'], window) &&
            path(['iosData', 'js-version'], window)) ||
        (ios && !safari) // checks if device is of ios but not running in safari browser
    ) {
        return 'iPhoneApp'
    }
    if (
        path(['sessionStorage'], window) &&
        typeof window.sessionStorage != 'undefined'
    ) {
        return sessionStorage.getItem('mobileApp')
    }
    return deviceType
}

export const getAppAgent = (deviceType: DeviceType) => {
    if (deviceType === 'DESKTOP') {
        return deviceType
    }

    let appAgent = 'PWA'
    const mobileDeviceType = checkDeviceType()
    if (mobileDeviceType === 'AndroidApp') {
        appAgent = path(['androidData', 'app-agent'], window)
    } else if (mobileDeviceType === 'iPhoneApp') {
        appAgent = path(['iosData', 'app-agent'], window)
    }

    return appAgent
}

export function getUserAuthValues() {
    const [email, profileName, gender, photo, userId] = decodeURIComponent(
        getCookie('userid') || ''
    ).split('|')
    return {
        email,
        profileName,
        gender,
        photo,
        userId
    }
}

type UserDataCookie = {
    firstName?: string
    displayName?: string
    fullName?: string
    email?: string
}

export function getUserDataCookieValues(): UserDataCookie {
    const userDataCookie = getCookie('userData') || ''
    if (!userDataCookie) return {}

    try {
        return JSON.parse(decodeURIComponent(userDataCookie))
    } catch (error) {
        return {}
    }
}

export function getAuthDisplayName() {
    const userData = getUserDataCookieValues()
    const { email } = getUserAuthValues()

    if (userData.firstName) return userData.firstName
    if (userData.displayName) return userData.displayName

    const fallbackEmail = userData.email || email
    if (fallbackEmail) {
        return fallbackEmail.split('@')[0]
    }

    return 'Hello'
}

const expireCookie = (name: string, domain?: string) => {
    if (typeof document === 'undefined') return
    const domainPart = domain ? `; domain=${domain}` : ''
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`
}

export function clearAuthCookies() {
    if (typeof window === 'undefined') return

    const domain = window.location.hostname.includes('flyin')
        ? '.flyin.com'
        : `.${window.location.hostname}`

    ;['userid', 'usermisc', 'userData'].forEach((cookieName) => {
        expireCookie(cookieName)
        expireCookie(cookieName, window.location.hostname)
        expireCookie(cookieName, domain)
    })
}

export const pluralize = (value: number, text: string, suffix = 's') =>
    value == 0 || value >= 1 ? text : `${text}${suffix}`

export function isLoggedIn() {
    const userObject = getUserAuthValues() || {}
    const usermiscVal = decodeURIComponent(getCookie('usermisc') || '').split(
        '|'
    )
    const signedIn =
        usermiscVal.includes('SIGNED_IN') &&
        userObject.userId &&
        userObject.userId.length > 0
            ? true
            : false
    return signedIn
}

export const isDateAfter = (firstDate: string, secondDate: string): boolean => {
    const date1 = new Date(firstDate)
    const date2 = new Date(secondDate)
    return date1 > date2
}

export const isMobileApp = () => {
    if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search)
        const appParam = urlParams.get('isApp')
        const mobileAppParam = urlParams.get('isMobileApp')
        const storedMobileApp = sessionStorage.getItem('isMobileApp') === 'true'
        const appCookie = getCookie('is_app_webview') === 'true'
        const shouldPersist =
            appParam === 'true' ||
            mobileAppParam === 'true' ||
            storedMobileApp ||
            appCookie

        if (shouldPersist) {
            sessionStorage.setItem('isMobileApp', 'true')
            document.cookie =
                'is_app_webview=true; path=/; max-age=2592000; SameSite=Lax'
            return true
        }
    }
    return false
}

export const buildVisaSignInUrl = (returnUrl?: string) => {
    const fallbackReturnUrl =
        typeof window !== 'undefined'
            ? `${window.location.origin}/visa`
            : '/visa'
    const targetReturnUrl =
        returnUrl ||
        (typeof window !== 'undefined'
            ? window.location.href
            : fallbackReturnUrl)

    const queryParams = new URLSearchParams()
    queryParams.set('returnUrl', targetReturnUrl)

    return `/en/signin?${queryParams.toString()}`
}

export const redirectToVisaSignIn = (returnUrl?: string) => {
    if (typeof window === 'undefined') return

    if (isMobileApp()) {
        try {
            if (typeof window?.MobileApp?.GO_TO_LOGIN === 'function') {
                window.MobileApp.GO_TO_LOGIN('')
                return
            }

            if (
                typeof window?.webkit?.messageHandlers?.GO_TO_LOGIN?.postMessage ===
                'function'
            ) {
                window.webkit.messageHandlers.GO_TO_LOGIN.postMessage('')
                return
            }
        } catch (error) {
            console.error('Failed to trigger app sign-in callback:', error)
        }
    }

    window.location.href = buildVisaSignInUrl(returnUrl)
}

export const navigateToVisaHome = () => {
    if (typeof window === 'undefined') return
    window.location.replace('/visa')
}

const FLYIN_SUPPORT_URL = '/contact.en.html'
const DEFAULT_SUPPORT_URL = '/support'

export const getSupportUrl = (brand: Brand) =>
    brand === FLYIN ? FLYIN_SUPPORT_URL : DEFAULT_SUPPORT_URL
